import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { PRIORITY, LABEL_KEYS } from '@dashboard/components/SyncMonitor/statuses';
import classes from './SyncCalendar.module.scss';

function worstStatus(statuses) {
  const found = PRIORITY.find((s) => statuses.includes(s));
  return found || 'no_data';
}

function getDaysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

// Monday-start: 0=Monday, 1=Tuesday, ..., 6=Sunday.
function getFirstDayOfMonth(year, month) {
  return (new Date(year, month - 1, 1).getDay() + 6) % 7;
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function dateKey(year, month, day) {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

function mergeForDay(calendarData, centerIds, dateStr) {
  const statuses = centerIds
    .map((id) => calendarData[id]?.[dateStr])
    .filter(Boolean);
  if (!statuses.length) return null;
  return worstStatus(statuses);
}

function describeDay(dateStr, status, t) {
  if (!status || status === 'empty') {
    return t('cellNoStatus', { date: dateStr });
  }
  const labelKey = LABEL_KEYS[status] || status;
  return t('cellWithStatus', { date: dateStr, status: t(labelKey) });
}

// ─── Yearly view ─────────────────────────────────────────────────────────────

function YearlyMonth({ year, monthIndex, calendarData, centerIds, onDayClick, t }) {
  const monthNum = monthIndex + 1;
  const days = getDaysInMonth(year, monthNum);
  const firstDay = getFirstDayOfMonth(year, monthNum);

  const cells = [];
  for (let i = 0; i < firstDay; i += 1) {
    cells.push(<span key={`e${i}`} className={classes.yearEmptyCell} />);
  }
  for (let d = 1; d <= days; d += 1) {
    const ds = dateKey(year, monthNum, d);
    const status = mergeForDay(calendarData, centerIds, ds) || 'empty';
    const description = describeDay(ds, status, t);
    cells.push(
      <button
        key={d}
        type="button"
        title={description}
        aria-label={description}
        data-tip={d}
        className={`${classes.yearDay} ${classes[`day_${status}`]}`}
        onClick={() => {
          if (centerIds.length === 1) onDayClick(centerIds[0], ds);
        }}
      />,
    );
  }

  const monthName = new Date(year, monthIndex, 1).toLocaleString('en', {
    month: 'short',
  });

  return (
    <div className={classes.yearMonth}>
      <div className={classes.yearMonthLabel}>{monthName}</div>
      <div className={classes.yearDayGrid}>{cells}</div>
    </div>
  );
}

YearlyMonth.propTypes = {
  year: PropTypes.number.isRequired,
  monthIndex: PropTypes.number.isRequired,
  calendarData: PropTypes.object.isRequired,
  centerIds: PropTypes.array.isRequired,
  onDayClick: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

function YearlyView({ year, calendarData, centerIds, onDayClick, t }) {
  return (
    <div className={classes.yearGrid}>
      {Array.from({ length: 12 }, (_, i) => (
        <YearlyMonth
          key={i}
          year={year}
          monthIndex={i}
          calendarData={calendarData}
          centerIds={centerIds}
          onDayClick={onDayClick}
          t={t}
        />
      ))}
    </div>
  );
}

YearlyView.propTypes = {
  year: PropTypes.number.isRequired,
  calendarData: PropTypes.object.isRequired,
  centerIds: PropTypes.array.isRequired,
  onDayClick: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

// ─── Monthly view ─────────────────────────────────────────────────────────────

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

function MonthlyView({ year, month, calendarData, centerIds, onDayClick, t }) {
  const days = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const cells = [];
  for (let i = 0; i < firstDay; i += 1) {
    cells.push(<div key={`e${i}`} className={classes.monthEmptyCell} />);
  }
  for (let d = 1; d <= days; d += 1) {
    const ds = dateKey(year, month, d);
    const status = mergeForDay(calendarData, centerIds, ds) || 'empty';
    const description = describeDay(ds, status, t);
    cells.push(
      <button
        key={d}
        type="button"
        title={description}
        aria-label={description}
        className={`${classes.monthDay} ${classes[`day_${status}`]}`}
        onClick={() => {
          if (centerIds.length === 1) onDayClick(centerIds[0], ds);
        }}
      >
        {d}
      </button>,
    );
  }

  return (
    <div className={classes.monthGrid}>
      {WEEKDAYS.map((wd) => (
        <div key={wd} className={classes.monthWeekday}>
          {wd}
        </div>
      ))}
      {cells}
    </div>
  );
}

MonthlyView.propTypes = {
  year: PropTypes.number.isRequired,
  month: PropTypes.number.isRequired,
  calendarData: PropTypes.object.isRequired,
  centerIds: PropTypes.array.isRequired,
  onDayClick: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

// ─── Main component ───────────────────────────────────────────────────────────

export default function SyncCalendar({
  calendarData,
  view,
  year,
  month,
  centerIds,
  onDayClick,
}) {
  const { t } = useTranslation('dashboard\\SyncMonitorView', {
    useSuspense: false,
  });

  if (view === 'month') {
    return (
      <MonthlyView
        year={year}
        month={month}
        calendarData={calendarData}
        centerIds={centerIds}
        onDayClick={onDayClick}
        t={t}
      />
    );
  }

  return (
    <YearlyView
      year={year}
      calendarData={calendarData}
      centerIds={centerIds}
      onDayClick={onDayClick}
      t={t}
    />
  );
}

SyncCalendar.propTypes = {
  calendarData: PropTypes.object.isRequired,
  view: PropTypes.oneOf(['year', 'month']).isRequired,
  year: PropTypes.number.isRequired,
  month: PropTypes.number.isRequired,
  centerIds: PropTypes.array.isRequired,
  onDayClick: PropTypes.func.isRequired,
};
